import * as Yup from 'yup';
import * as console from 'node:console';
import { Form, useFormik, FormikProvider } from 'formik';
import React, { useState, useEffect, SyntheticEvent } from 'react';

import TabList from '@mui/lab/TabList';
import Alert from '@mui/material/Alert';
import { LoadingButton } from '@mui/lab';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Tab,
  Card,
  Grid,
  Stack,
  Checkbox,
  MenuItem,
  Accordion,
  TextField,
  Typography,
  AccordionDetails,
  AccordionSummary,
  FormControlLabel,
} from '@mui/material';

import useAuth from 'src/hooks/useAuth';
import useLocales from 'src/hooks/useLocales';
import useResponsive from 'src/hooks/useResponsive';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
import useFormTranslate from 'src/hooks/useFormTranslate';
import useNotifications from 'src/hooks/useNotifications';

import eventBus, { EventTypes } from 'src/utils/eventBus';
import {
  dataUrlToFile,
  containerHasMimetype,
  getAllContainerFilesBase64,
} from 'src/utils/fileUtils';

import { useSelector } from 'src/redux/store';
import documentService from 'src/services/documentService';
import { ModuleTypes } from 'src/@types/module/enums/moduleTypes';
import { Permissions } from 'src/@types/auth/constants/permissions';
import { clearSignPosition } from 'src/redux/slices/signaturePosition';
import { FileExtensions } from 'src/@types/fileView/enums/fileExtensions';
import { DocumentSignatureType } from 'src/@types/documents/enums/documentSignatureTypes';
import { setTab, SignType, setShowTabs, setSignType } from 'src/redux/slices/documentSigner';
import {
  DSFile,
  EndorseRequest,
  AcknowledgeRequest,
  ApproveInitRequest,
  SmartIdInitSignatureRequest,
  MobileIdInitSignatureRequest,
  EParakstsInitSignatureRequest,
} from 'src/@types/documents/types';
import {
  signByHand,
  removeSession,
  approveDocument,
  endorseDocument,
  acknowledgeDocument,
  initSmartIdSignatureSession,
  initMobileIdSignatureSession,
  initEParakstsSignatureSession,
} from 'src/redux/slices/signatureSession';

import Tip from 'src/components/Tip';
import ModuleCheck from 'src/components/access-checks/ModuleCheck';
import PermissionCheck from 'src/components/access-checks/PermissionCheck';

import ControlCodePopup from 'src/sections/auth/control-code/ControlCodePopup';

import DocumentVoidDialog from './DocumentVoidDialog';
import DocumentSmartIdSign from './DocumentSmartIdSign';
import DocumentMobileIdSign from './DocumentMobileIdSign';
import HandwrittenSignature from './HandwrittenSignature';
import useAppSettings from '../../../hooks/useAppSettings';
import DocumentEParakstsSign from './DocumentEParakstsSign';
import DocumentRejectionDialog from './DocumentRejectionDialog';
import { requestedSignatureType } from './requestedSignatureType';
import DocumentEIDEasySigningDialog from './DocumentEIDEasySigningDialog';
import { increaseDocumentReloadState } from '../../../redux/slices/document';
import { clearSignatureState } from '../../../redux/slices/handwrittenSignature';
import { SignatureFormatLevel } from '../../../@types/documents/enums/documentSignatareLevel';

// ----------------------------------------------------------------------

type FormValues = {
  timestamp: string;
  stampPage: string;
};

export enum TimeStamps {
  TopLeft = 'TOP_LEFT',
  // BottomLeft = 'BOTTOM_LEFT',
  // TopRight = 'TOP_RIGHT',
  BottomRight = 'BOTTOM_RIGHT',
}

export enum TimeStampPages {
  First = 'FIRST',
  Last = 'LAST',
}

export enum Tabs {
  MobileId = 'MOBILE_ID',
  SmartId = 'SMART_ID',
  EParaksts = 'EPARAKSTS',
  EParakstsCard = 'EPARAKSTS_CARD',
  Approve = 'APPROVE',
  Acknowledge = 'ACKNOWLEDGE',
  Endorse = 'ENDORSE',
  SignaturePad = 'SIGNATURE_PAD',
  EIDEasy = 'EID_EASY',
}

type DocumentSigningProps = {
  isWorkflowDriven: boolean;
};

const isNonSigningType = (signatureType?: DocumentSignatureType | null) =>
  signatureType === DocumentSignatureType.Acknowledge ||
  signatureType === DocumentSignatureType.Endorse;

export default function DocumentSigning({ isWorkflowDriven }: DocumentSigningProps) {
  const { t, i18n } = useLocales();
  const { user } = useAuth();
  const [submitMobileIdTrigger, setSubmitMobileIdTrigger] = useState(0);
  const [submitSmartIdTrigger, setSubmitSmartIdTrigger] = useState(0);
  const [submitEParakstsTrigger, setSubmitEParakstsTrigger] = useState(0);
  const [allowSign, setAllowSign] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [canSign, setCanSign] = useState(false);
  const [canAcknowledge, setCanAcknowledge] = useState(false);
  const [canEndorse, setCanEndorse] = useState(false);
  const isMountedRef = useIsMountedRef();
  const { selectedId, document } = useSelector((state) => state.document);
  const { signatureImg, signatureImgSize, signatureImgPosition, touched, page } = useSelector(
    (state) => state.handwrittenSignature
  );
  const { error, sessionId, isStatusStarted } = useSelector((state) => state.signatureSession);
  const { settings } = useSelector((state) => state.company);
  const isMobile = useResponsive('down', 'sm');
  const { enqueueSuccess, enqueueError } = useNotifications();
  const [isRejectionDialogOpen, setIsRejectionDialogOpen] = useState(false);
  const [loginMethod, setLoginMethod] = useState<string | null>(null);
  const { signers, signType, tab, showTabs } = useSelector((state) => state.documentSigner);
  const modulesData = useAppSettings();
  const [canUserSelectTimestampPosition, setCanUserSelectTimestampPosition] = useState(true);
  const [canUserSelectTimestampPagePosition, setCanUserSelectTimestampPagePosition] =
    useState(true);
  const [needSign, setNeedSign] = useState(false);
  const [commentValue, setCommentValue] = useState<string>('');
  const [extendedSignatureStamp, setExtendedSignatureStamp] = useState(false);
  const ltaSignatureLevel = document?.highestSignatureLevel === SignatureFormatLevel.LTA;
  const [isOpen, setIsOpen] = useState(false);

  const pendingSigner = signers.find(
    (signer) =>
      signer.email === user?.email &&
      !signer.signed &&
      !signer.rejected &&
      !(isWorkflowDriven && isNonSigningType(signer.signatureType))
  );
  const allowedTabs = requestedSignatureType(pendingSigner?.signatureType, {
    isEidEasyAvailable:
      modulesData.modules.includes(ModuleTypes.EidEasy) &&
      (document?.extension === 'pdf' || document?.extension === 'asice'),
    isSignaturePadAvailable: document?.extension === 'pdf',
  });

  const handleCommentValueChange = (event: any) => {
    const { value } = event.target;
    if (value.length <= 100) {
      setCommentValue(value);
    }
  };

  const handleTabChange = (_: SyntheticEvent, tab: Tabs) => {
    setTab(tab);
  };

  useEffect(() => {
    getNeedSign(selectedId).then();
  }, [selectedId, isRejectionDialogOpen]);

  useEffect(() => {
    const documentSignedSubscription = eventBus.subscribe(EventTypes.DocumentSigned, () => {
      enqueueSuccess('documents.snackbar.signedSuccessfully');
    });

    const documentApprovedSubscription = eventBus.subscribe(EventTypes.DocumentApproved, () => {
      enqueueSuccess('documents.snackbar.approvedSuccessfully');
    });

    const forbidSignSubscription = eventBus.subscribe(EventTypes.ForbidSign, () => {
      setAllowSign(false);
    });

    const loginMethodLocalStorage = localStorage.getItem('loginMethod');
    setLoginMethod(loginMethodLocalStorage);

    return () => {
      documentSignedSubscription.unsubscribe();
      documentApprovedSubscription.unsubscribe();
      forbidSignSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (signers.length !== 0) {
      setAvailableTabs();
    }
  }, [loginMethod, signers, document, signType, canAcknowledge, canEndorse, isWorkflowDriven]);

  useEffect(() => {}, [signers]);

  useEffect(() => {
    // what's the main point of this useEffect?
    const signerIndex = signers.findIndex((s) => s.email === document?.ownerEmail);
    const isSigned = signers.some((s) => s.email === user?.email && s.signed);

    if (signerIndex === -1) {
      setAllowSign(false);
      setAvailableTabs();
    }

    if (signerIndex > -1 && !isSigned) {
      setAllowSign(true);
      setAvailableTabs();
    }

    if (canAcknowledge || canEndorse) {
      setAllowSign(true);
    }

    // when signers change, with this we see if the signer can sign the doc
    if (document) {
      getNeedSign(document.id).then();
    }
  }, [signers, canAcknowledge, canEndorse]);

  const handleTabsByLoginMethod = (loginMethod: string) => {
    if (loginMethod.toString() === 'EPARAKSTS') {
      return { EPARAKSTS: true, EPARAKSTS_CARD: true };
    }

    return {
      [loginMethod.toString()]: true,
      EID_EASY: !!modulesData.modules.includes(ModuleTypes.EidEasy),
    };
  };

  useEffect(() => {
    if (
      tab !== Tabs.MobileId &&
      tab !== Tabs.SmartId &&
      tab !== Tabs.EParaksts &&
      tab !== Tabs.EParakstsCard &&
      tab !== Tabs.EIDEasy
    ) {
      clearSignPosition();
    }
  }, [tab]);

  const isOwner = () => user?.email === document?.ownerEmail;

  useEffect(() => {}, [allowSign]);

  const setAvailableTabs = () => {
    if (allowedTabs) {
      setTab(allowedTabs.initialTab as Tabs);
      setShowTabs(allowedTabs.showTabs);

      return;
    }

    if (loginMethod) {
      // Acknowledge

      if (signType === SignType.handWrittenSign) {
        setTab(Tabs.SignaturePad);
        setShowTabs({
          ...showTabs,
          MOBILE_ID: false,
          SMART_ID: false,
          EPARAKSTS: false,
          EPARAKSTS_CARD: false,
          EID_EASY: false,
          [loginMethod.toString()]: false,
          APPROVE: false,
          SIGNATURE_PAD: true,
        });
      } else if (canAcknowledge) {
        setShowTabs({
          ...showTabs,
          [loginMethod.toString()]: false,
          APPROVE: false,
          ACKNOWLEDGE: true,
          SIGNATURE_PAD: false,
        });
        setTab(Tabs.Acknowledge);
      } else if (canEndorse) {
        setShowTabs({
          ...showTabs,
          [loginMethod.toString()]: false,
          APPROVE: false,
          ENDORSE: true,
          SIGNATURE_PAD: false,
        });
        setTab(Tabs.Endorse);
        // Sign, Approve
      } else {
        const ownerIsOnlySigner = signers.length === 1 && isOwner();

        // Document extension => pdf
        if (document?.extension === 'pdf') {
          // Login method => qualified
          if (loginMethod !== Tabs.Approve) {
            if (ownerIsOnlySigner) {
              if (signType === SignType.onlyApprove) {
                setTab(Tabs.Approve);
                setShowTabs({
                  ...showTabs,
                  MOBILE_ID: false,
                  SMART_ID: false,
                  EPARAKSTS: false,
                  EPARAKSTS_CARD: false,
                  APPROVE: true,
                  SIGNATURE_PAD: false,
                  EID_EASY: false,
                });
              } else {
                setTab(loginMethod as Tabs);
                setShowTabs({
                  ...showTabs,
                  ...handleTabsByLoginMethod(loginMethod),
                  APPROVE: false,
                  SIGNATURE_PAD: false,
                });
              }
            } else {
              if (qualifiedSignatureAllowed()) {
                setShowTabs({
                  ...showTabs,
                  ...handleTabsByLoginMethod(loginMethod),
                  APPROVE: false,
                  SIGNATURE_PAD: false,
                });
                setTab(loginMethod as Tabs);
              } else if (signType === SignType.onlyApprove) {
                setShowTabs({
                  ...showTabs,
                  MOBILE_ID: false,
                  SMART_ID: false,
                  EPARAKSTS: false,
                  EPARAKSTS_CARD: false,
                  APPROVE: true,
                  SIGNATURE_PAD: false,
                  EID_EASY: false,
                });
                setTab(Tabs.Approve);
              }
            }
            // Login method => NOT qualified
          } else {
            if (ownerIsOnlySigner) {
              if (signType === SignType.onlyApprove) {
                setShowTabs({
                  ...showTabs,
                  MOBILE_ID: false,
                  SMART_ID: false,
                  EPARAKSTS: false,
                  EPARAKSTS_CARD: false,
                  APPROVE: true,
                  SIGNATURE_PAD: false,
                  EID_EASY: false,
                });
                setTab(Tabs.Approve);
              } else {
                setTab(Tabs.MobileId);
                setShowTabs({
                  ...showTabs,
                  MOBILE_ID: true,
                  SMART_ID: true,
                  EPARAKSTS: true,
                  EPARAKSTS_CARD: true,
                  EID_EASY: !!modulesData.modules.includes(ModuleTypes.EidEasy),
                  APPROVE: false,
                  SIGNATURE_PAD: false,
                });
              }
            } else {
              // kaip cia veiks
              if (qualifiedSignatureAllowed()) {
                setTab(Tabs.MobileId);
                setShowTabs({
                  ...showTabs,
                  MOBILE_ID: true,
                  SMART_ID: true,
                  EPARAKSTS: true,
                  EPARAKSTS_CARD: true,
                  EID_EASY: !!modulesData.modules.includes(ModuleTypes.EidEasy),
                  APPROVE: false,
                  SIGNATURE_PAD: false,
                });
              } else {
                setTab(Tabs.Approve);
                setShowTabs({ ...showTabs, APPROVE: true });
              }
            }
          }
          // Document extension => NOT pdf
        } else {
          if (loginMethod !== Tabs.Approve) {
            if (ownerIsOnlySigner) {
              setSignType(SignType.onlyQualified);
            }

            setTab(loginMethod as Tabs);
            setShowTabs({
              ...showTabs,
              ...handleTabsByLoginMethod(loginMethod),
              ...(ownerIsOnlySigner && {
                EID_EASY: !!modulesData.modules.includes(ModuleTypes.EidEasy),
              }),
              APPROVE: false,
              SIGNATURE_PAD: false,
            });
          } else {
            setSignType(SignType.onlyQualified);
            setTab(Tabs.MobileId);
            setShowTabs({
              ...showTabs,
              MOBILE_ID: true,
              SMART_ID: true,
              EPARAKSTS: true,
              EPARAKSTS_CARD: true,
              EID_EASY: !!modulesData.modules.includes(ModuleTypes.EidEasy),
              APPROVE: false,
              SIGNATURE_PAD: false,
            });
          }
        }
      }
    }
  };

  useEffect(() => {
    if (error) {
      enqueueError(error);
    }
  }, [error]);

  async function getNeedSign(id: number | null) {
    if (!id) {
      setAllowSign(false);
      return;
    }
    try {
      const data = await documentService.getNeedSign(id);
      setAllowSign(data.needSign);
      setCanSign(data.canSign);
      setCanAcknowledge(data.canAcknowledge);
      setCanEndorse(data.canEndorse);
      setNeedSign(data.needSign);
    } catch (error) {
      setAllowSign(false);
      setCanSign(false);
      setCanAcknowledge(false);
      setCanEndorse(false);
    }
  }

  const clearSession = async () => {
    setAllowSign(false);
    await removeSession(sessionId);
  };

  useEffect(() => {}, [allowSign]);

  const validationSchema = Yup.object().shape({
    timestamp: Yup.string().required(),
    stampPage: Yup.string().required(),
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      timestamp: TimeStamps.TopLeft,
      stampPage: TimeStampPages.First,
    },
    validationSchema,

    onSubmit: () => {
      if (!canSign) {
        return;
      }
      submitTriggerNext();
    },
    onReset: async () => {
      setAllowSign(false);
    },
  });

  const { values, handleSubmit, getFieldProps, setFieldValue, resetForm, setSubmitting } = formik;

  useFormTranslate(formik);

  function submitTriggerNext() {
    switch (tab) {
      case Tabs.MobileId:
        setSubmitMobileIdTrigger(submitMobileIdTrigger + 1);
        break;
      case Tabs.SmartId:
        setSubmitSmartIdTrigger(submitSmartIdTrigger + 1);
        break;
      case Tabs.EParaksts:
      case Tabs.EParakstsCard:
        setSubmitEParakstsTrigger(submitEParakstsTrigger + 1);
        break;
      case Tabs.Approve:
        submitValues({ signatureType: DocumentSignatureType.Unqualified }).then();
        break;
      case Tabs.Acknowledge:
        submitValues({ signatureType: DocumentSignatureType.Acknowledge }).then();
        break;
      case Tabs.Endorse:
        submitValues({ signatureType: DocumentSignatureType.Endorse }).then();
        break;
      case Tabs.SignaturePad:
        submitValues({ signatureType: DocumentSignatureType.Handwritten }).then();
        break;

      default:
        break;
    }
  }

  function qualifiedSignatureAllowed() {
    return !document?.isOnlyApprove;
  }

  const handleRejectionDialogClose = () => {
    setIsRejectionDialogOpen(false);
  };

  const submitValues = async (idValues: any = null) => {
    if (!document) {
      return;
    }

    setIsLoading(true);

    try {
      // api eparaksts with sign comment
      if (commentValue && tab === Tabs.EParaksts) {
        await documentService.saveComment(document.id, commentValue);
      }

      const initSign = await documentService.getInitSign(
        selectedId as number,
        idValues.signatureType
      );
      if (!initSign || initSign?.isBlocked) {
        setIsLoading(false);
        enqueueError('documents.snackbar.documentIsBeingSigned');
        setAllowSign(false);
        return;
      }

      const initFile = await dataUrlToFile(
        `data:application/zip;base64,${initSign.file}`,
        document.name,
        'application/zip'
      );
      let files: DSFile[];
      if (
        [FileExtensions.Asic, FileExtensions.Bdoc, FileExtensions.Edoc].includes(
          document.extension as FileExtensions
        ) &&
        !(await containerHasMimetype(initFile))
      ) {
        const zipFiles = await getAllContainerFilesBase64(initFile);
        files = zipFiles.map(
          (zf) =>
            ({
              content: zf.content,
              name: zf.name,
            }) as DSFile
        );
      } else {
        files = [
          {
            content: initSign.file,
            name: document.name || '',
          } as DSFile,
        ];
      }

      const timestampLogo = settings?.timestampImage || undefined;
      const code = settings?.code || '';
      const address = settings?.address || '';

      switch (tab) {
        case Tabs.MobileId:
          const mobileIdData = {
            fileExtension: document.extension ?? FileExtensions.Pdf,
            documentId: selectedId as number,
            personalCode: idValues.inn,
            phone: idValues.phone,
            files,
            documentProps: [
              { id: document.id, highestSignatureLevel: document.highestSignatureLevel },
            ],
            timestampPosition: values.timestamp,
            timestampImage: timestampLogo,
            companyCode: document.extension === FileExtensions.Adoc ? code : undefined,
            companyAddress: document.extension === FileExtensions.Adoc ? address : undefined,
            companyName: document.extension === FileExtensions.Adoc ? document.name : undefined,
            language: i18n.language,
            documentName: document.name,
            timestampPage: values.stampPage,
            signerPosition: idValues.position,
            signerSubdivision: idValues.subdivision,
            // signCoordinates: [positionX, positionY, signPage],
            comment: commentValue,
            extendedSignatureStamp,
          } as MobileIdInitSignatureRequest;

          await initMobileIdSignatureSession(mobileIdData);
          clearSignPosition();
          break;

        case Tabs.SmartId:
          const smartIdData = {
            fileExtension: document.extension ?? FileExtensions.Pdf,
            documentId: selectedId as number,
            personalCode: idValues.personalCode,
            countryCode: idValues.countryCode,
            files,
            documentProps: [
              { id: document.id, highestSignatureLevel: document.highestSignatureLevel },
            ],
            timestampPosition: values.timestamp,
            timestampImage: timestampLogo,
            companyCode: document.extension === FileExtensions.Adoc ? code : undefined,
            companyAddress: document.extension === FileExtensions.Adoc ? address : undefined,
            companyName: document.extension === FileExtensions.Adoc ? document.name : undefined,
            language: i18n.language,
            documentName: document.name,
            timestampPage: values.stampPage,
            signerPosition: idValues.position,
            signerSubdivision: idValues.subdivision,
            // signCoordinates: [positionX, positionY, signPage],
            comment: commentValue,
            extendedSignatureStamp,
          } as SmartIdInitSignatureRequest;
          await initSmartIdSignatureSession(smartIdData);
          clearSignPosition();
          break;

        case Tabs.EParaksts:
        case Tabs.EParakstsCard:
          const eParakstsData = {
            signType: tab === Tabs.EParaksts ? 0 : 1,
            fileExtension: document.extension ?? FileExtensions.Pdf,
            documentId: selectedId as number,
            personalCode: idValues.personalCode,
            countryCode: idValues.personalCode.includes('-') ? 'LV' : 'LT',
            files,
            documentProps: [
              { id: document.id, highestSignatureLevel: document.highestSignatureLevel },
            ],
            timestampPosition: values.timestamp,
            timestampImage: timestampLogo,
            companyCode: document.extension === FileExtensions.Adoc ? code : undefined,
            companyAddress: document.extension === FileExtensions.Adoc ? address : undefined,
            companyName: document.extension === FileExtensions.Adoc ? document.name : undefined,
            language: i18n.language,
            documentName: document.name,
            timestampPage: values.stampPage,
            originUrl: `${window.location.href}`,
            signCoordinates: [0, 0, 0],
            comment: commentValue,
            documents: [{ id: document.id, type: document.extension ?? FileExtensions.Pdf }],
          } as EParakstsInitSignatureRequest;

          await initEParakstsSignatureSession(eParakstsData);
          clearSignPosition();
          break;

        case Tabs.Approve:
          const approveData = {
            fileExtension: document.extension ?? FileExtensions.Pdf,
            documentId: selectedId as number,
            files,
            documentProps: [
              { id: document.id, highestSignatureLevel: document.highestSignatureLevel },
            ],
            timestampPosition: values.timestamp,
            timestampImage: timestampLogo,
            language: i18n.language,
            documentName: document.name,
            timestampPage: values.stampPage,
            comment: commentValue,
          } as ApproveInitRequest;
          await approveDocument(approveData);
          break;

        case Tabs.Acknowledge:
          const acknowledgeData = {
            documentId: selectedId as number,
            comment: commentValue,
          } as AcknowledgeRequest;
          await acknowledgeDocument(acknowledgeData);
          setCanAcknowledge(false);
          break;

        case Tabs.Endorse:
          const endorseData = {
            documentId: selectedId as number,
            comment: commentValue,
          } as EndorseRequest;
          await endorseDocument(endorseData);
          setCanEndorse(false);
          break;

        case Tabs.SignaturePad:
          const model = {
            documentId: document.id,
            stamp: signatureImg.split(',').pop(),
            x: signatureImgPosition ? Math.round(signatureImgPosition.x) : 0,
            y: signatureImgPosition ? Math.round(signatureImgPosition.y) : 0,
            page: page - 1,
            width: signatureImgSize ? Math.round(signatureImgSize.width) : 0,
            height: signatureImgSize ? Math.round(signatureImgSize.height) : 0,
          };

          try {
            await signByHand(model);
          } catch (error) {
            console.error(error);
          } finally {
            clearSignatureState();
            increaseDocumentReloadState();
          }
          break;

        default:
          return;
      }

      setSubmitting(false);
      setAllowSign(false);
      setIsLoading(false);
      resetForm();
    } catch (error) {
      setAllowSign(true);
      setIsLoading(false);
      resetForm();
      if (isMountedRef.current) {
        setSubmitting(false);
        setAllowSign(false);
      }
    }
  };

  const onSectionClick = () => {
    eventBus.publish({
      topic: EventTypes.SigningBlockClicked,
      payload: {},
    });
  };

  const getTooltipTitle = () =>
    tab === Tabs.Approve
      ? 'documents.buttons.tooltip.approve'
      : canSign
        ? 'documents.buttons.tooltip.sign'
        : 'documents.buttons.tooltip.disabledSign';

  const setTimestamp = (timestamp: string) => {
    if (timestamp !== ' ') {
      setCanUserSelectTimestampPosition(false);
      setFieldValue('timestamp', timestamp);
    }
  };

  const setStampPage = (stampPage: string) => {
    if (stampPage !== ' ') {
      setCanUserSelectTimestampPagePosition(false);
      setFieldValue('stampPage', stampPage);
    }
  };

  useEffect(() => {
    if (settings?.timestampPosition) {
      setTimestamp(settings.timestampPosition);
    }

    if (settings?.timestampPagePosition) {
      setStampPage(settings.timestampPagePosition);
    }
  }, [settings]);

  const btnText = () => {
    if (tab === Tabs.Acknowledge) {
      return t('documents.buttons.acknowledge');
    }
    if (tab === Tabs.Endorse) {
      return t('documents.buttons.endorse');
    }
    return t(tab === Tabs.Approve ? 'documents.buttons.approve' : 'documents.buttons.sign');
  };

  const isDisabledBtn = () => {
    if (tab === Tabs.Approve) {
      return false;
    }

    if (tab === Tabs.SignaturePad) {
      return !touched;
    }

    if ((tab === Tabs.EParaksts || tab === Tabs.EParakstsCard) && document?.extension === 'adoc') {
      return true;
    }

    if ((tab === Tabs.EParaksts || tab === Tabs.EParakstsCard) && ltaSignatureLevel) {
      return true;
    }

    return !canSign;
  };

  return (
    <>
      <ControlCodePopup onClose={clearSession} />
      {allowSign && needSign && (
        <Box onClick={onSectionClick}>
          <Card>
            <Accordion defaultExpanded sx={{ p: 3, display: 'block' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 3 }}>
                <Typography variant="h6"> {t('documents.signing')}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pt: 0 }}>
                {document && document.isFileProtected && (
                  <Alert sx={{ mb: 2 }} severity="error">
                    {t('documents.securedDocument')}
                  </Alert>
                )}

                {document && !document.isFileProtected && (
                  <TabContext value={tab || ''}>
                    <TabList variant="scrollable" sx={{ mb: 2 }} onChange={handleTabChange}>
                      {showTabs.EID_EASY &&
                        !canAcknowledge && !canEndorse &&
                        (document.extension === 'pdf' || document.extension === 'asice') && (
                          <Tab
                            sx={[
                              {
                                textTransform: 'none',
                              },
                              isMobile
                                ? {
                                    marginRight: '8px !important',
                                  }
                                : {
                                    marginRight: null,
                                  },
                            ]}
                            label={
                              <Box>
                                <img
                                  width={24}
                                  height={24}
                                  style={{
                                    margin: '8px auto',
                                    filter: !modulesData.modules.includes(ModuleTypes.EidEasy)
                                      ? 'grayscale(100%)'
                                      : '',
                                    opacity: !modulesData.modules.includes(ModuleTypes.EidEasy)
                                      ? '0.4'
                                      : '',
                                  }}
                                  src="/icons/icon_dlx.svg"
                                  alt="eid_easy"
                                />
                                {t('shared.fields.dlx')}
                              </Box>
                            }
                            value={Tabs.EIDEasy}
                            disabled={!modulesData.modules.includes(ModuleTypes.EidEasy)}
                          />
                        )}

                      {showTabs.MOBILE_ID && !canAcknowledge && !canEndorse && (
                        <Tab
                          sx={[
                            {
                              textTransform: 'none',
                            },
                            isMobile
                              ? {
                                  marginRight: '8px !important',
                                }
                              : {
                                  marginRight: null,
                                },
                          ]}
                          label={
                            <Box>
                              <img
                                width={24}
                                height={24}
                                style={{ margin: '8px auto' }}
                                src="/icons/icon_mobile_id.svg"
                                alt="mobile_id"
                              />
                              {t('shared.fields.mobileId')}
                            </Box>
                          }
                          value={Tabs.MobileId}
                        />
                      )}

                      {showTabs.SMART_ID && !canAcknowledge && !canEndorse && (
                        <Tab
                          sx={[
                            {
                              textTransform: 'none',
                            },
                            isMobile
                              ? {
                                  marginRight: '8px !important',
                                }
                              : {
                                  marginRight: null,
                                },
                          ]}
                          label={
                            <Box>
                              <img
                                width={24}
                                height={24}
                                style={{ margin: '8px auto' }}
                                src="/icons/icon_smart_id.svg"
                                alt="smart_id"
                              />
                              {t('shared.fields.smartId')}
                            </Box>
                          }
                          value={Tabs.SmartId}
                        />
                      )}

                      {showTabs.EPARAKSTS && !canAcknowledge && !canEndorse && (
                        <Tab
                          sx={[
                            {
                              textTransform: 'none',
                            },
                            isMobile
                              ? {
                                  marginRight: '8px !important',
                                }
                              : {
                                  marginRight: null,
                                },
                          ]}
                          label={
                            <Box>
                              <img
                                width={24}
                                height={24}
                                style={{ margin: '8px auto' }}
                                src="/icons/icon_eparakst_mobile.png"
                                alt="eParaksts Mobile"
                              />
                              {t('shared.fields.eparakstsMobile')}
                            </Box>
                          }
                          value={Tabs.EParaksts}
                        />
                      )}

                      {showTabs.EPARAKSTS_CARD && !canAcknowledge && !canEndorse && (
                        <Tab
                          sx={[
                            {
                              textTransform: 'none',
                            },
                            isMobile
                              ? {
                                  marginRight: '8px !important',
                                }
                              : {
                                  marginRight: null,
                                },
                          ]}
                          label={
                            <Box>
                              <img
                                width={24}
                                height={24}
                                style={{ margin: '8px auto' }}
                                src="/icons/icon_eparakst.svg"
                                alt="eParaksts Card"
                              />
                              {t('shared.fields.eparakstsCard')}
                            </Box>
                          }
                          value={Tabs.EParakstsCard}
                        />
                      )}

                      {showTabs.APPROVE && (
                        <Tab
                          sx={[
                            {
                              textTransform: 'none',
                            },
                            isMobile
                              ? {
                                  marginRight: '8px !important',
                                }
                              : {
                                  marginRight: null,
                                },
                          ]}
                          label={
                            <Box>
                              <img
                                width={28}
                                height={28}
                                style={{ margin: '3px auto 5px auto' }}
                                src="/icons/icon_approve.svg"
                                alt="approve"
                              />
                              {t('documents.approve')}
                            </Box>
                          }
                          value={Tabs.Approve}
                        />
                      )}

                      {showTabs.SIGNATURE_PAD && (
                        <Tab
                          sx={[
                            {
                              textTransform: 'none',
                            },
                            isMobile
                              ? {
                                  marginRight: '8px !important',
                                }
                              : {
                                  marginRight: null,
                                },
                          ]}
                          label={
                            <Box>
                              <img
                                width={28}
                                height={28}
                                style={{ margin: '3px auto 5px auto' }}
                                src="/icons/icon_handwritten_sign.svg"
                                alt="hand written sign"
                              />
                              {t('documents.signaturePad')}
                            </Box>
                          }
                          value={Tabs.SignaturePad}
                        />
                      )}

                      {showTabs.ACKNOWLEDGE && (
                        <Tab
                          sx={[
                            {
                              textTransform: 'none',
                            },
                            isMobile
                              ? {
                                  marginRight: '8px !important',
                                }
                              : {
                                  marginRight: null,
                                },
                          ]}
                          label={
                            <Box>
                              <img
                                width={28}
                                height={28}
                                style={{ margin: '3px auto 5px auto' }}
                                src="/icons/icon_approve.svg"
                                alt="acknowledge"
                              />
                              {t('documents.acknowledge')}
                            </Box>
                          }
                          value={Tabs.Acknowledge}
                        />
                      )}

                      {showTabs.ENDORSE && (
                        <Tab
                          sx={[
                            {
                              textTransform: 'none',
                            },
                            isMobile
                              ? {
                                  marginRight: '8px !important',
                                }
                              : {
                                  marginRight: null,
                                },
                          ]}
                          label={
                            <Box>
                              <img
                                width={28}
                                height={28}
                                style={{ margin: '3px auto 5px auto' }}
                                src="/icons/icon_approve.svg"
                                alt="endorse"
                              />
                              {t('documents.buttons.endorse')}
                            </Box>
                          }
                          value={Tabs.Endorse}
                        />
                      )}
                    </TabList>

                    <Box>
                      <Stack>
                        <Box>
                          <TabPanel value={Tabs.EIDEasy} />

                          {showTabs.MOBILE_ID && !canAcknowledge && !canEndorse && !showTabs.SIGNATURE_PAD && (
                            <TabPanel value={Tabs.MobileId}>
                              <DocumentMobileIdSign
                                submitTrigger={submitMobileIdTrigger}
                                submitValues={submitValues}
                                isAdoc={document?.extension === 'adoc'}
                              />
                            </TabPanel>
                          )}
                          {showTabs.SMART_ID && !canAcknowledge && !canEndorse && !showTabs.SIGNATURE_PAD && (
                            <TabPanel value={Tabs.SmartId}>
                              <DocumentSmartIdSign
                                submitTrigger={submitSmartIdTrigger}
                                submitValues={submitValues}
                                isAdoc={document?.extension === 'adoc'}
                              />
                            </TabPanel>
                          )}
                          {showTabs.EPARAKSTS && !canAcknowledge && !canEndorse && !showTabs.SIGNATURE_PAD && (
                            <TabPanel value={Tabs.EParaksts}>
                              <DocumentEParakstsSign
                                submitTrigger={submitEParakstsTrigger}
                                submitValues={submitValues}
                              />
                            </TabPanel>
                          )}
                          {showTabs.EPARAKSTS_CARD &&
                            !canAcknowledge && !canEndorse &&
                            !showTabs.SIGNATURE_PAD && (
                              <TabPanel value={Tabs.EParakstsCard}>
                                <DocumentEParakstsSign
                                  submitTrigger={submitEParakstsTrigger}
                                  submitValues={submitValues}
                                />
                              </TabPanel>
                            )}
                          {showTabs.SIGNATURE_PAD && !canAcknowledge && !canEndorse && (
                            <TabPanel value={Tabs.SignaturePad}>
                              <HandwrittenSignature />
                            </TabPanel>
                          )}
                          {showTabs.APPROVE && <TabPanel value={Tabs.Approve} />}
                          {showTabs.ACKNOWLEDGE && <TabPanel value={Tabs.Acknowledge} />}
                          {showTabs.ENDORSE && <TabPanel value={Tabs.Endorse} />}
                        </Box>

                        <FormikProvider value={formik}>
                          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                            {document?.extension === FileExtensions.Pdf &&
                              tab !== Tabs.Acknowledge &&
                              tab !== Tabs.Endorse &&
                              tab !== Tabs.SignaturePad && (
                                <TextField
                                  disabled={!canUserSelectTimestampPosition}
                                  select
                                  fullWidth
                                  size="small"
                                  label={t('documents.fields.timestampPosition')}
                                  {...getFieldProps('timestamp')}
                                  sx={{ mb: 3, mt: 3 }}
                                  id="301032mj"
                                >
                                  <MenuItem value={TimeStamps.TopLeft}>
                                    {t('documents.timestampPositionValues.topLeft')}
                                  </MenuItem>

                                  <MenuItem value={TimeStamps.BottomRight}>
                                    {t('documents.timestampPositionValues.bottomRight')}
                                  </MenuItem>
                                </TextField>
                              )}

                            {document?.extension === FileExtensions.Pdf &&
                              tab !== Tabs.Acknowledge &&
                              tab !== Tabs.Endorse &&
                              tab !== Tabs.SignaturePad && (
                                <TextField
                                  disabled={!canUserSelectTimestampPagePosition}
                                  select
                                  fullWidth
                                  size="small"
                                  label={t('documents.fields.timestampPagePosition')}
                                  {...getFieldProps('stampPage')}
                                >
                                  <MenuItem value={TimeStampPages.First}>
                                    {t('documents.timestampPagePositionValues.firstPage')}
                                  </MenuItem>
                                  <MenuItem value={TimeStampPages.Last}>
                                    {t('documents.timestampPagePositionValues.lastPage')}
                                  </MenuItem>
                                </TextField>
                              )}

                            <Grid
                              container
                              spacing={2}
                              sx={{
                                justifyContent: 'space-between',
                                mt: 1,
                              }}
                            >
                              {/* ----- Signing comment ----- */}
                              <ModuleCheck module={ModuleTypes.SigningComment}>
                                <Grid sx={{ mt: 2 }} size={12}>
                                  <TextField
                                    variant="outlined"
                                    fullWidth
                                    placeholder={t('shared.fields.signingCommment')}
                                    label={t('shared.fields.signingCommment')}
                                    size="small"
                                    multiline
                                    name="comment"
                                    value={commentValue}
                                    onChange={handleCommentValueChange}
                                    id="qfcosi0u"
                                    helperText={`${commentValue ? commentValue.length : 0}/100 ${t('forms.validations.numberOfCharacters')}`}
                                    maxRows={4}
                                    slotProps={{
                                      htmlInput: {
                                        maxLength: 100,
                                      },
                                    }}
                                  />
                                </Grid>
                              </ModuleCheck>

                              {(tab === 'SMART_ID' || tab === 'MOBILE_ID') && (
                                <ModuleCheck module={ModuleTypes.ExtendedStampModule}>
                                  <Grid size={12}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={extendedSignatureStamp}
                                          onChange={(e) =>
                                            setExtendedSignatureStamp(e.target.checked)
                                          }
                                        />
                                      }
                                      label={t('documents.extendedStamp')}
                                    />
                                  </Grid>
                                </ModuleCheck>
                              )}

                              {isOwner() && (
                                <ModuleCheck module={ModuleTypes.VoidDocuments}>
                                  <PermissionCheck permissions={[Permissions.VoidDocument]}>
                                    <Grid size="grow">
                                      <LoadingButton
                                        fullWidth
                                        type="button"
                                        onClick={() => setIsRejectionDialogOpen(true)}
                                        variant="outlined"
                                        loading={isLoading || isStatusStarted}
                                        id="n6cda3m5"
                                      >
                                        {t('documents.buttons.void')}
                                      </LoadingButton>
                                      {isRejectionDialogOpen && (
                                        <DocumentVoidDialog
                                          handleClose={handleRejectionDialogClose}
                                        />
                                      )}
                                    </Grid>
                                  </PermissionCheck>
                                </ModuleCheck>
                              )}

                              {!isOwner() && (
                                <Grid size="grow">
                                  <LoadingButton
                                    fullWidth
                                    type="button"
                                    onClick={() => setIsRejectionDialogOpen(true)}
                                    variant="outlined"
                                    loading={isLoading || isStatusStarted}
                                    id="mysjgf38"
                                  >
                                    {t('documents.buttons.reject')}
                                  </LoadingButton>
                                  {isRejectionDialogOpen && (
                                    <DocumentRejectionDialog
                                      handleClose={handleRejectionDialogClose}
                                    />
                                  )}
                                </Grid>
                              )}
                              <Grid size="grow">
                                <Tip titleLocale={getTooltipTitle()}>
                                  {tab === Tabs.EIDEasy && (
                                    <LoadingButton
                                      fullWidth
                                      variant="contained"
                                      loading={isLoading || isStatusStarted}
                                      disabled={isDisabledBtn() || document?.extension === 'adoc'}
                                      id="zecmav9p"
                                      onClick={() => setIsOpen(true)}
                                    >
                                      {btnText()}
                                    </LoadingButton>
                                  )}

                                  {tab !== Tabs.EIDEasy && (
                                    <LoadingButton
                                      fullWidth
                                      type="submit"
                                      variant="contained"
                                      loading={isLoading || isStatusStarted}
                                      disabled={
                                        isDisabledBtn() ||
                                        (tab === Tabs.EParaksts && document?.extension === 'adoc')
                                      }
                                      id="zecmav3p"
                                    >
                                      {btnText()}
                                    </LoadingButton>
                                  )}
                                </Tip>
                              </Grid>
                            </Grid>
                          </Form>
                        </FormikProvider>
                      </Stack>
                    </Box>
                  </TabContext>
                )}

                {(tab === Tabs.EParaksts || tab === Tabs.EParakstsCard) && ltaSignatureLevel && (
                  <Alert severity="error" sx={{ mt: 4 }}>
                    {t('documents.massApproving.errors.ltaSignatureLevelWarning')}
                  </Alert>
                )}
              </AccordionDetails>
            </Accordion>
          </Card>
        </Box>
      )}
      {!allowSign &&
        !needSign &&
        isOwner() &&
        document &&
        document.voided === null &&
        document.status !== 1 && (
          <Box onClick={onSectionClick}>
            <Card>
              <Accordion defaultExpanded sx={{ p: 3 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 3 }}>
                  <Typography variant="h6"> {t('documents.signing')}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pt: 0 }}>
                  <TabContext value={tab || ''}>
                    <Box>
                      <Stack>
                        <FormikProvider value={formik}>
                          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                            <Grid
                              container
                              spacing={3}
                              sx={{
                                justifyContent: 'space-between',
                                mt: 1,
                              }}
                            >
                              {isOwner() && (
                                <ModuleCheck module={ModuleTypes.VoidDocuments}>
                                  <PermissionCheck permissions={[Permissions.VoidDocument]}>
                                    <Grid size="grow">
                                      <LoadingButton
                                        fullWidth
                                        type="button"
                                        onClick={() => setIsRejectionDialogOpen(true)}
                                        variant="outlined"
                                        loading={isLoading || isStatusStarted}
                                        sx={{ mt: -3 }}
                                        id="0p2ymmzz"
                                      >
                                        {t('documents.buttons.void')}
                                      </LoadingButton>
                                      {isRejectionDialogOpen && (
                                        <DocumentVoidDialog
                                          handleClose={handleRejectionDialogClose}
                                        />
                                      )}
                                    </Grid>
                                  </PermissionCheck>
                                </ModuleCheck>
                              )}
                              {!isOwner() && (
                                <Grid size="grow">
                                  <LoadingButton
                                    fullWidth
                                    type="button"
                                    onClick={() => setIsRejectionDialogOpen(true)}
                                    variant="outlined"
                                    loading={isLoading || isStatusStarted}
                                    id="u8tlecgq"
                                  >
                                    {t('documents.buttons.reject')}
                                  </LoadingButton>
                                  {isRejectionDialogOpen && (
                                    <DocumentRejectionDialog
                                      handleClose={handleRejectionDialogClose}
                                    />
                                  )}
                                </Grid>
                              )}
                            </Grid>
                          </Form>
                        </FormikProvider>
                      </Stack>
                    </Box>
                  </TabContext>
                </AccordionDetails>
              </Accordion>
            </Card>
          </Box>
        )}
      <DocumentEIDEasySigningDialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        timestampPosition={formik.values.timestamp as TimeStamps}
        timestampPage={formik.values.stampPage as TimeStampPages}
      />
    </>
  );
}
